import { computed, onScopeDispose, ref, toValue, type MaybeRefOrGetter } from 'vue'

import { CreativeBenchmarkApi } from '../api/CreativeBenchmarkApi'
import { BENCHMARK_DEFAULT_DIMENSIONS, type BenchmarkDimension } from '../models/benchmark.types'
import { benchmarkBandOf } from '../service/creative/benchmarkBands'
import { buildBenchmarkDateRequest } from '../service/creative/benchmarkDateRange'
import { mergeBenchmarkRows } from '../service/creative/mergeBenchmarkRows'

import { useWorkspace } from '@/features/account'
import type { ReportDataLoader, ReportResponse, ReportRow } from '@/features/reports'

const DEFAULT_TIMEOUT_MS = 5_000

const MEDIA_DIMENSIONS = ['preview_ss_url', 'image_url', 'thumbnail_url']

const DIMENSION_FIELDS: Record<BenchmarkDimension, string> = {
  LOCATION: 'location',
  PLACEMENT: 'placement',
  AD_CATEGORY: 'ad_category',
  EXTERNAL_ACTION: 'external_action',
}

export interface UseCreativeBenchmarkOptions {
  dimensions: MaybeRefOrGetter<readonly BenchmarkDimension[]>
  defaultPostData: MaybeRefOrGetter<string>
  api?: Pick<CreativeBenchmarkApi, 'fetchBenchmark'>
  benchmarkTimeoutMs?: number
}

type BenchmarkOutcome =
  | { kind: 'ready'; records: Awaited<ReturnType<CreativeBenchmarkApi['fetchBenchmark']>> }
  | { kind: 'failed' }
  | { kind: 'slow' }

function responseRows(response: ReportResponse): ReportRow[] {
  return response.combined?.data ?? response.data
}

function withRows(response: ReportResponse, rows: ReportRow[]): ReportResponse {
  return response.combined
    ? { ...response, combined: { ...response.combined, data: rows } }
    : { ...response, data: rows }
}

/** Interceptor-free benchmark orchestration around module 08's single report funnel. */
export function useCreativeBenchmark(options: UseCreativeBenchmarkOptions) {
  const workspace = useWorkspace()
  const api = options.api ?? new CreativeBenchmarkApi()
  const notice = ref(false)
  let activeBenchmark: AbortController | null = null
  let generation = 0

  const selectedDimensions = computed(() => [...toValue(options.dimensions)])
  const dimensionFields = computed(() =>
    selectedDimensions.value.map((dimension) => DIMENSION_FIELDS[dimension]),
  )
  const dateRequest = computed(() => buildBenchmarkDateRequest(toValue(options.defaultPostData)))
  const isDirty = computed(() => {
    if (dateRequest.value.compareTimeWindow !== 30) return true
    if (selectedDimensions.value.length !== BENCHMARK_DEFAULT_DIMENSIONS.length) return true
    const selected = new Set(selectedDimensions.value)
    return BENCHMARK_DEFAULT_DIMENSIONS.some((dimension) => !selected.has(dimension))
  })
  const requestDimensions = computed(() => [
    'ad_id',
    ...MEDIA_DIMENSIONS,
    ...(isDirty.value ? [] : dimensionFields.value),
  ])

  const dataLoader: ReportDataLoader = async ({ signal, load }) => {
    activeBenchmark?.abort()
    activeBenchmark = null
    const currentGeneration = ++generation
    notice.value = false

    // Start the controller immediately. Defaults use its inline p_* values and never touch the
    // custom endpoint.
    const reportPromise = load()
    if (!isDirty.value) return reportPromise

    const workspaceId = workspace.workspaceId.value
    if (workspaceId === undefined) return reportPromise

    const controller = new AbortController()
    activeBenchmark = controller
    const abortBenchmark = (): void => controller.abort()
    if (signal.aborted) abortBenchmark()
    else signal.addEventListener('abort', abortBenchmark, { once: true })

    const benchmarkPromise: Promise<BenchmarkOutcome> = api
      .fetchBenchmark(
        Number(workspaceId),
        {
          dimensions: [...selectedDimensions.value],
          compare_time_window: dateRequest.value.compareTimeWindow,
        },
        controller.signal,
      )
      .then((records) => ({ kind: 'ready' as const, records }))
      .catch(() => ({ kind: 'failed' as const }))

    let timeout: ReturnType<typeof setTimeout> | null = null
    const slow = new Promise<BenchmarkOutcome>((resolve) => {
      timeout = setTimeout(
        () => resolve({ kind: 'slow' }),
        options.benchmarkTimeoutMs ?? DEFAULT_TIMEOUT_MS,
      )
    })

    try {
      const [response, outcome] = await Promise.all([
        reportPromise,
        Promise.race([benchmarkPromise, slow]),
      ])
      if (outcome.kind === 'slow') controller.abort()
      if (signal.aborted || currentGeneration !== generation) return response
      if (outcome.kind !== 'ready') {
        notice.value = true
        return response
      }
      return withRows(response, mergeBenchmarkRows(responseRows(response), outcome.records))
    } finally {
      if (timeout) clearTimeout(timeout)
      signal.removeEventListener('abort', abortBenchmark)
      if (activeBenchmark === controller) activeBenchmark = null
    }
  }

  onScopeDispose(() => {
    generation += 1
    activeBenchmark?.abort()
  })

  return {
    isDirty,
    dimensionFields,
    requestDimensions,
    reportPostData: computed(() => dateRequest.value.postData),
    notice,
    dataLoader,
    bandOf: benchmarkBandOf,
  }
}
